import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';

import '../contracts/Payroll_ReceiptsInput';
import '../contracts/Payroll_DeleteReceiptResponse';
import '../contracts/Payroll_ReceiptsResponse';
/**
 * Composable for receipt management (Azure Blob Storage)
 */
export function useReceipts() {
  const receipts = ref<number[]>([]);
  const isUploading = ref(false);
  const uploadProgress = ref(0);
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Upload receipt file
   * @param {number} executionId
   * @param {File} file
   * @returns {Promise<Object>}
   */
  const uploadReceipt = async (executionId: any, file: any) => {
    isUploading.value = true;
    uploadProgress.value = 0;
    error.value = null;

    log('useReceipts', 'uploadReceipt', 'Uploading receipt', {
      executionId,
      fileName: file.name,
      fileSize: file.size
    });

    try {
      // Validate file
      const validationErrors = validateFile(file);
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(', '));
      }

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('receipt', file);
      formData.append('execution_id', executionId);

      const response = await httpService.post('/payroll/receipts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent.total) {
            uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          }
        },
        inputContract: 'Payroll_ReceiptsInput',
        responseContract: 'Payroll_ReceiptsResponse'
      } as any);

      if (!response.success) {
        throw new Error(response.error || 'Failed to upload receipt');
      }

      const receipt = response.dados as { id?: number | string } | undefined;
      log('useReceipts', 'uploadReceipt', 'Receipt uploaded', { receiptId: receipt?.id });

      return receipt;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('useReceipts', 'uploadReceipt', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isUploading.value = false;
      uploadProgress.value = 0;
    }
  };

  /**
   * List receipts for an execution
   * @param {number} executionId
   * @returns {Promise<Array>}
   */
  const listReceipts = async (executionId: number) => {
    isLoading.value = true;
    error.value = null;

    log('useReceipts', 'listReceipts', 'Listing receipts', { executionId });

    try {
      const response = await httpService.get('/payroll/receipts', {
        params: { execution_id: executionId },
        responseContract: 'Payroll_ReceiptsResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to list receipts');
      }

      receipts.value = response.dados || [];
      log('useReceipts', 'listReceipts', 'Receipts loaded', { count: receipts.value.length });

      return receipts.value;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('useReceipts', 'listReceipts', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete a receipt
   * @param {number} receiptId
   * @param {number} franchiseId
   * @returns {Promise<boolean>}
   */
  const deleteReceipt = async (receiptId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('useReceipts', 'deleteReceipt', 'Deleting receipt', { receiptId, franchiseId });

    try {
      const response = await httpService.delete(`/payroll/delete_receipt/${receiptId}`, {
        params: { franchise_id: franchiseId },
        responseContract: 'Payroll_DeleteReceiptResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete receipt');
      }

      log('useReceipts', 'deleteReceipt', 'Receipt deleted', { receiptId });
      return true;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('useReceipts', 'deleteReceipt', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Validate file before upload
   * @param {File} file
   * @returns {Array<string>} - Array of error messages
   */
  const validateFile = (file: any) => {
    const errors = [];

    // File required
    if (!file) {
      errors.push('File is required');
      return errors;
    }

    // Check file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      errors.push('File size must not exceed 10MB');
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      errors.push('File must be JPEG, PNG, or PDF');
    }

    return errors;
  };

  /**
   * Format file size for display
   * @param {number} bytes
   * @returns {string}
   */
  const formatFileSize = (bytes: any) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return {
    // State
    receipts,
    isUploading,
    uploadProgress,
    isLoading,
    error,

    // Methods
    uploadReceipt,
    listReceipts,
    deleteReceipt,
    validateFile,

    // Helpers
    formatFileSize
  };
}
